import React, { useState, useEffect, useCallback } from 'react';
import { FlexGroup, FlexItem, Code, Icon, Text, useTectonTheme, Loading, ViewLayout } from '@lithos';
import styled from '@emotion/styled';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetRepoFiles } from '../../api/workspace';

interface FileNode {
  name: string;
  path: string;
  isDirectory: boolean;
  children?: FileNode[];
  content?: string;
}

interface FileTreeProps {
  node: FileNode;
  depth: number;
  onFileSelect: (path: string) => void;
  selectedFile: string | null;
}

const TreeItemWrapper = styled.div<{ depth: number; isSelected: boolean }>`
  cursor: pointer;
  padding-left: ${({ depth }) => depth * 20}px;
  padding: ${({ theme }) => theme.v1.size['1']}px;
  padding-left: ${({ depth, theme }) => depth * 20 + theme.v1.size['1']}px;
  background-color: ${({ theme, isSelected }) => (isSelected ? theme.v1.colors.background.accent : 'transparent')};

  &:hover {
    background-color: ${({ theme }) => theme.v1.colors.background.hover};
  }
`;

const FileTreeItem: React.FC<FileTreeProps> = ({ node, depth, onFileSelect, selectedFile }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = () => {
    if (node.isDirectory) {
      setIsExpanded(!isExpanded);
    } else {
      onFileSelect(node.path);
    }
  };

  const isSelected = selectedFile === node.path;

  return (
    <>
      <TreeItemWrapper depth={depth} isSelected={isSelected} onClick={handleToggle}>
        <FlexGroup direction="row" alignItems="center" gapSize="1">
          <Icon icon={node.isDirectory ? 'Folder' : 'Code'} size="s" type="mono" />
          <Text>{node.name}</Text>
        </FlexGroup>
      </TreeItemWrapper>
      {node.isDirectory &&
        isExpanded &&
        node.children &&
        node.children.map((child, index) => (
          <FileTreeItem
            key={index}
            node={child}
            depth={depth + 1}
            onFileSelect={onFileSelect}
            selectedFile={selectedFile}
          />
        ))}
    </>
  );
};

const buildFileTree = (files: { [path: string]: string }): FileNode[] => {
  const root: FileNode = {
    name: 'root',
    path: '',
    isDirectory: true,
    children: [],
  };

  Object.keys(files).forEach((path) => {
    const parts = path.split('/').filter(Boolean);
    let current = root;

    parts.forEach((part, index) => {
      const isLastPart = index === parts.length - 1;
      const currentPath = parts.slice(0, index + 1).join('/');

      let existingChild = current.children?.find((child) => child.name === part);

      if (!existingChild) {
        existingChild = {
          name: part,
          path: currentPath,
          isDirectory: !isLastPart,
          children: !isLastPart ? [] : undefined,
          content: isLastPart ? files[path] : undefined,
        };
        current.children?.push(existingChild);
      }

      current = existingChild;
    });
  });

  return root.children || [];
};

const RepoFiles: React.FC = () => {
  const { theme } = useTectonTheme();
  const params = useParams<{ '*': string; workspace: string }>();
  const navigate = useNavigate();
  const [fileTree, setFileTree] = useState<FileNode[] | null>(null);
  const [files, setFiles] = useState<{ [path: string]: string }>({});
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [invalidFilePath, setInvalidFilePath] = useState<string | null>(null);
  // Debug logging
  console.log('RepoFiles render - workspace:', params.workspace, 'fileTree length:', fileTree?.length || 0);

  // Get file path from URL params
  const getFilePathFromUrl = useCallback((): string | null => {
    const urlPath = params['*'];
    if (!urlPath) return null;
    try {
      return decodeURIComponent(urlPath);
    } catch {
      return urlPath;
    }
  }, [params]);

  const {
    data: repoFiles,
    isLoading,
    error: apiError,
  } = useGetRepoFiles({
    enabled: true, // Auto-fetch when component loads
  });

  // Debug logging for API data
  console.log('API Response - isLoading:', isLoading, 'repoFiles files:', repoFiles?.files?.length || 0);

  // Process files when repo files are available
  useEffect(() => {
    const repoData = repoFiles as { files?: Array<{ path: string; content: string }> };
    if (repoData?.files) {
      console.log('Processing files from GraphQL:', repoData.files.length);

      // Convert files array to object
      const filesObj: { [path: string]: string } = {};
      repoData.files.forEach((file: { path: string; content: string }) => {
        filesObj[file.path] = file.content;
      });

      // Build file tree
      const tree = buildFileTree(filesObj);
      setFileTree(tree);
      setFiles(filesObj);
    }
  }, [repoFiles]);

  // Set selected file from URL on component mount and when file tree is ready
  useEffect(() => {
    const urlFilePath = getFilePathFromUrl();
    if (urlFilePath && files[urlFilePath] && selectedFile !== urlFilePath) {
      setSelectedFile(urlFilePath);
    }
  }, [files, getFilePathFromUrl, selectedFile]);

  // Update selected file from URL changes
  useEffect(() => {
    const urlFilePath = getFilePathFromUrl();
    if (urlFilePath !== selectedFile) {
      if (urlFilePath && files[urlFilePath]) {
        setSelectedFile(urlFilePath);
        setInvalidFilePath(null); // Clear any previous invalid path
      } else if (urlFilePath && Object.keys(files).length > 0) {
        // File path exists in URL but file doesn't exist in the data
        setInvalidFilePath(urlFilePath);
        setSelectedFile(null);
      } else if (!urlFilePath) {
        setSelectedFile(null);
        setInvalidFilePath(null);
      }
    }
  }, [params, files, selectedFile, getFilePathFromUrl]);

  const handleFileSelect = (filePath: string) => {
    setSelectedFile(filePath);
    setInvalidFilePath(null); // Clear any invalid path when selecting a valid file
    // Update URL to reflect selected file
    try {
      const encodedPath = encodeURIComponent(filePath);
      navigate(encodedPath, { replace: true });
    } catch {
      navigate(filePath, { replace: true });
    }
  };

  let content;

  if (isLoading) {
    content = <Loading />;
  } else if (apiError) {
    content = <Text>API Error: {String(apiError)}</Text>;
  } else if (repoFiles && !fileTree) {
    content = (
      <FlexGroup direction="column" gapSize="3">
        <Text element="h3">Repository Information</Text>
        <Text>Commit ID: {repoFiles.commitId}</Text>
        <Text>SDK Version: {repoFiles.sdkVersion}</Text>
        <Text>Processing repository files...</Text>
      </FlexGroup>
    );
  } else if (fileTree) {
    content = (
      <FlexGroup
        direction="row"
        gapSize="0"
        css={{
          height: '100vh',
        }}
      >
        <FlexItem
          basis="300px"
          grow={0}
          shrink={0}
          css={{
            borderRight: `1px solid ${theme.v1.colors.border.primary}`,
            overflow: 'auto',
            backgroundColor: theme.v1.colors.background.empty,
          }}
        >
          <FlexGroup direction="column" gapSize="3" padding="3">
            <FlexGroup direction="column" gapSize="0">
              {fileTree.map((node, index) => (
                <FileTreeItem
                  key={index}
                  node={node}
                  depth={0}
                  onFileSelect={handleFileSelect}
                  selectedFile={selectedFile}
                />
              ))}
            </FlexGroup>
          </FlexGroup>
        </FlexItem>
        <FlexItem grow={1} css={{ overflow: 'auto' }}>
          <FlexGroup direction="column" gapSize="3" padding="3">
            {invalidFilePath ? (
              <Text>File Not Found</Text>
            ) : selectedFile ? (
              <>
                <Text element="h4">{selectedFile}</Text>
                <Code code={files[selectedFile] || 'No content available'} language="typescript" />
              </>
            ) : (
              <Text>Select a file to view its contents</Text>
            )}
          </FlexGroup>
        </FlexItem>
      </FlexGroup>
    );
  }

  return (
    <ViewLayout
      header={{
        title: 'Repository Files',
        breadcrumbs: {
          root: { label: params.workspace || 'workspace', url: `/repo/${params.workspace}` },
          items: [],
        },
      }}
      content={content}
    />
  );
};

export default RepoFiles;
